import {
  customerRequestConfirmPath,
  customerRequestDetailPath,
  customerRequestProcessPath,
} from "../../../routes"

/** Where a waiting/open/gaps dashboard row's ACTION button should land.
 *
 *  Driven by the row's raw lifecycle `state` (COALESCE(processing_status,
 *  status) — present on all three row types) and, for `review_ready`, the
 *  row's `step` (only populated on waiting/gaps rows — see
 *  CustomerDashboardWaitingItem.step; OpenItem rows have no step). The goal
 *  is to route DIRECTLY to the process step the row is promising, instead of
 *  bouncing through RecordView's "Continue processing" CTA:
 *   - needs_confirm / extraction_failed -> the confirm/import flow (intake
 *     step — RecordView renders no actionable CTA for these, see below)
 *   - mapping_ready / reconcile_ready -> the matching process step directly
 *   - review_ready -> step 'gaps' -> the gaps step; step 'review' -> the
 *     review step; no step (e.g. OpenWidget rows) -> the bare process path,
 *     which QuestionnaireProcessingLayout auto-redirects to the canonical
 *     step for the current processing_status (processingSteps.ts,
 *     canonicalStepForStatus + isStepReachable)
 *   - answered -> the review step (ReviewStep is also where "Send" lives —
 *     there is no separate send step in PROCESSING_STEP_ORDER)
 *   - everything else (stored_pending, failed_*, in-flight statuses
 *     mapping/reconciling/answering/rendering, sent, unknown/undefined) ->
 *     the record page. stored_pending's "Start Processing" CTA and the
 *     failed_* states (no retry mechanism in the process view) are the
 *     record page's job; in-flight statuses have nothing useful to land a
 *     user on mid-flight either.
 *
 *  Title links are NOT routed through this helper — they keep going straight
 *  to the record route (or the customer-filtered list when a customer name
 *  is known), since that's a read-only "see more" affordance, not the row's
 *  primary call to action. */
export function requestActionPath(item: {
  questionnaireId: string
  state?: string
  step?: string
}): string {
  const { questionnaireId, state, step } = item
  switch (state) {
    case "needs_confirm":
    case "extraction_failed":
      return customerRequestConfirmPath(questionnaireId)
    case "mapping_ready":
      return customerRequestProcessPath(questionnaireId, "mapping")
    case "reconcile_ready":
      return customerRequestProcessPath(questionnaireId, "reconcile")
    case "review_ready":
      if (step === "gaps")
        return customerRequestProcessPath(questionnaireId, "gaps")
      if (step === "review")
        return customerRequestProcessPath(questionnaireId, "review")
      return customerRequestProcessPath(questionnaireId)
    case "answered":
      return customerRequestProcessPath(questionnaireId, "review")
    default:
      return customerRequestDetailPath(questionnaireId)
  }
}
